/*
  Related products carousel.
  The list renders as the normal product grid; Swiper is only switched on for the
  breakpoints where the section settings ask for it (desktop >= 990px, mobile below).
  Swiper classes are added/removed at runtime so grid mode stays untouched.
*/
if (!customElements.get('related-products-slider')) {
  class RelatedProductsSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.related-products__viewport');
      this.list = this.viewport && this.viewport.querySelector('.product-grid');
      if (!this.list) return;

      this.prevButton = this.querySelector('.related-products__nav--prev');
      this.nextButton = this.querySelector('.related-products__nav--next');

      this.onPrev = () => this.swiper && this.swiper.slidePrev();
      this.onNext = () => this.swiper && this.swiper.slideNext();
      this.onBreakpointChange = () => this.update();
      this.updateNav = this.updateNav.bind(this);

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      this.mql = window.matchMedia('(min-width: 990px)');
      this.mql.addEventListener('change', this.onBreakpointChange);

      if (typeof Swiper === 'undefined' && document.readyState !== 'complete') {
        window.addEventListener('load', this.onBreakpointChange, { once: true });
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.mql) this.mql.removeEventListener('change', this.onBreakpointChange);
      window.removeEventListener('load', this.onBreakpointChange);
      this.destroy();
    }

    update() {
      const isDesktop = this.mql.matches;
      const wanted = isDesktop ? this.dataset.desktop === 'true' : this.dataset.mobile === 'true';

      this.destroy();
      if (!wanted) return;

      if (typeof Swiper === 'undefined') {
        // Swiper failed to load: fall back to a native horizontal scroll.
        if (document.readyState === 'complete') this.classList.add('related-products__slider--fallback');
        return;
      }

      this.create(isDesktop);
    }

    create(isDesktop) {
      const rootStyles = getComputedStyle(document.documentElement);
      const gapMobile = parseFloat(rootStyles.getPropertyValue('--grid-mobile-horizontal-spacing')) || 8;
      const gapDesktop = parseFloat(rootStyles.getPropertyValue('--grid-desktop-horizontal-spacing')) || 16;
      const slidesPerView = isDesktop
        ? parseFloat(this.dataset.spvDesktop) || 4
        : parseFloat(this.dataset.spvMobile) || 1.5;

      this.classList.remove('related-products__slider--fallback');
      this.viewport.classList.add('swiper');
      this.list.classList.add('swiper-wrapper');
      Array.from(this.list.children).forEach((item) => item.classList.add('swiper-slide'));

      this.swiper = new Swiper(this.viewport, {
        slidesPerView,
        spaceBetween: gapMobile,
        breakpoints: {
          750: { spaceBetween: gapDesktop },
        },
        watchOverflow: true,
        grabCursor: true,
        resistanceRatio: 0.85,
        a11y: { enabled: true, slideRole: 'listitem' },
        keyboard: { enabled: true, onlyInViewport: true },
      });

      ['slideChange', 'reachBeginning', 'reachEnd', 'resize', 'lock', 'unlock'].forEach((event) =>
        this.swiper.on(event, this.updateNav)
      );
      this.updateNav();
    }

    destroy() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (!this.list) return;

      this.viewport.classList.remove('swiper');
      this.list.classList.remove('swiper-wrapper');
      Array.from(this.list.children).forEach((item) => item.classList.remove('swiper-slide'));
      this.updateNav();
    }

    updateNav() {
      if (!this.prevButton || !this.nextButton) return;

      const swiper = this.swiper;
      const hidden = !swiper || !this.mql.matches || swiper.isLocked;
      this.prevButton.hidden = hidden;
      this.nextButton.hidden = hidden;
      if (hidden) return;

      this.prevButton.disabled = swiper.isBeginning;
      this.nextButton.disabled = swiper.isEnd;
    }
  }

  customElements.define('related-products-slider', RelatedProductsSlider);
}
